import React from "react"

import Layout from "../components/layout"
import CardGrid from "../components/cardGrid"
import Slalom from "../components/slalom"
import ContentBlock from "../components/contentBlock"
import SEO from "../components/seo"
import { graphql, useStaticQuery } from "gatsby"
import EventCalendar from "../components/eventCalendar"
import SocialProof from "../components/socialProof"
import DeveloperContent from "../components/DeveloperContent"
import CustomLink from "../components/customLink"
import { defaultEndCap } from "../components/EndCap/constants"

const topCards = [
  <div className="cardContent-flex">
    <h4>Fast and lightweight</h4>
    <p>
      Build data rich apps without draining device resources with Realm's lazy
      loading and zero-copy architecture.
    </p>
  </div>,
  <div className="cardContent-flex">
    <h4>Simple to start and scale</h4>
    <p>
      The object-oriented data model lets developers work directly with native
      objects – no ORMs or DAOs needed.
    </p>
  </div>,
  <div className="cardContent-flex">
    <h4>Built-in mobile to cloud sync</h4>
    <p>
      Real-time mobile to cloud data sync makes it easy to build interactive features that keep data up-to-date across multiple devices, users, and the backend.
    </p>
  </div>,
]

const middleCards = [
  {
    content: (
      <div className="cardContent">
        <h3>Learning from scratch</h3>
        <p>
          See Realm in action in this task tracker tutorial where you’ll learn
          how to make a collaborative task tracker app using Realm and Realm
          Sync.
        </p>
        <CustomLink
          faux
          href="https://docs.mongodb.com/realm/tutorial/"
          text="Read tutorial"
        />
      </div>
    ),
    link: "https://docs.mongodb.com/realm/tutorial/",
  },
  {
    content: (
      <div className="cardContent">
        <h3>Advanced Realm guides</h3>
        <p>
          If you’re already familiar with Realm, read our documentation to
          increase your skills.
        </p>
        <CustomLink
          faux
          href="https://docs.mongodb.com/realm/sdk/"
          text="View docs"
        />
      </div>
    ),
    link: "https://docs.mongodb.com/realm/sdk/",
  },
  {
    content: (
      <div className="cardContent">
        <h3>Configuring Realm Sync</h3>
        <p>
          View our guides and documentation to learn how to enable Realm Sync
          for your Realm app.
        </p>
        <CustomLink
          faux
          href="https://docs.mongodb.com/realm/sync/learn/overview/"
          text="Learn about Realm Sync"
        />
      </div>
    ),
    link: "https://docs.mongodb.com/realm/sync/learn/overview/",
  },
  {
    content: (
      <div className="cardContent">
        <h3>Github repos</h3>
        <p>
          Check out readmes and sample repos or start a feature discussion on
          Github.
        </p>
        <CustomLink faux href="https://github.com/realm" text="View repos" />
      </div>
    ),
    link: "https://github.com/realm",
  },
]

const bottomCards = [
  <div className="cardContent-flex">
    <h4>What is Realm?</h4>
    <p>
      Realm is an object-oriented mobile database built to make storing,
      querying, and syncing data simple. No ORMs or DAOs required.
    </p>
  </div>,

  <div className="cardContent-flex">
    <h4>What languages and frameworks does Realm support?</h4>
    <p>
      Realm supports a variety of languages including Swift, Objective-C, Kotlin, Java, JavaScript, React Native, Xamarin, C#, Dart, Flutter, .NET for C#, and Unity.
    </p>
  </div>,

  <div className="cardContent-flex">
    <h4>Is Realm just a wrapper on top of SQLite?</h4>
    <p>
      No, Realm is built from the ground up as a modern, performant alternative
      to SQLite.
    </p>
    <br />
    <h4>Is Realm ACID-compliant?</h4>
    <p>Yes, Realm is ACID-compliant.</p>
  </div>,

  <div className="cardContent-flex">
    <h4>What is MongoDB’s relationship with Realm?</h4>
    <p>
      MongoDB acquired Realm in 2019 and has since released Realm Sync – a mobile 
      to cloud synchronization solution that syncs data from Realm to
      MongoDB’s fully managed cloud offering, Atlas.
    </p>
    <p>
      MongoDB is committed to keeping the Realm database open source and free to
      use.
    </p>
  </div>,
]

const slaloms = [
  {
    content: (
      <>
        <h3>Launch faster with intuitive APIs</h3>
        <p>
          Accelerate time-to-market with easy to learn, language-native APIs
          that simplify data access.
        </p>
      </>
    ),
    codeLanguages: [
      {
        name: "Kotlin",
        snippets: [
          {
            code:
              '// Your object classes are your database schema definition\nopen class Dog(\n    var name: String = "",\n    var age: Int = 0\n): RealmObject()\n\nopen class Person(\n    @PrimaryKey var _id: String = "",\n    var name: String = "",\n    var age: Int = 0,\n    // Create relationships by pointing an Object field to another Class\n    var dogs: RealmList&#60;Dog&#62; = RealmList()\n): RealmObject()\n\n// Use Realm objects like regular Kotlin objects\nval dog = Dog()\ndog.name = "Rex"\ndog.age = 1\n\n// Initialize Realm (just once per application)\nRealm.init(context)\n\n// Get a Realm instance for this thread\nrealm = Realm.getDefaultInstance()\n\n// Add the dog to the realm\nrealm.executeTransaction {\n    it.copyToRealm(dog)\n}',
            position: "1",
          },
        ],
      },
      {
        name: "Swift",
        snippets: [
          {
            code:
              '// Define your models like regular Swift classes\nclass Dog: Object {\n    @objc dynamic var name = ""\n    @objc dynamic var age = 0\n}\nclass Person: Object {\n    @objc dynamic var _id = ""\n    @objc dynamic var name = ""\n    @objc dynamic var age = 0\n    // Create relationships by pointing an Object field to another Class\n    let dogs = List&#60;Dog&#62;()\n    \n    override static func primaryKey() -> String? {\n        return "_id"\n    }\n}\n// Use them like regular Swift objects\nlet dog = Dog()\ndog.name = "Rex"\ndog.age = 1\nprint("name of dog: \\(dog.name)")\n\n// Get the default Realm\nlet realm = try! Realm()\n// Persist your data easily with a write transaction \ntry! realm.write {\n    realm.add(dog)\n}',
            position: "1",
          },
        ],
      },
      {
        name: "JavaScript",
        snippets: [
          {
            code:
              '// Your object classes are your database schema definition\nconst Dog = {\n  name: "Dog",\n  properties: {\n    name: "string",\n    age: "int",\n    breed: "string?"\n  }\n};\n\nconst Person = {\n  name: "Person",\n  primaryKey: \'_id\',\n  properties: {\n    _id: "string",\n    name: "string",\n    age: "int",\n    // Create relationships by pointing an Object field to another Class\n    dogs: "Dog[]"\n  }\n};\n\n// Open a Realm\nconst realm = new Realm({schema: [Dog, Person]});\n\n// persist dog to the realm with a simple write transaction\nrealm.write(() => {\n\n  // Create Realm objects and use them just like regular JS objects\n  const dog = realm.create("Dog", { name: "Rex", age: 1 });\n});\n',
            position: "1",
          },
        ],
      },
      {
        name: "C#",
        snippets: [
          {
            code:
              '// Define your models like regular C# classes\npublic class Dog : RealmObject\n{\n    public string Name { get; set; }\n    public int Age { get; set; }\n}\n\npublic class Person : RealmObject\n{\n    [PrimaryKey]\n    public string Id { get; set; }\n    public string Name { get; set; }\n    public int Age { get: set;}\n    // Create relationships by pointing an Object field to another Class\n    public IList&#60;Dog&#62; Dogs { get; }\n}\n\n// Open a Realm reference\nvar realm = Realm.GetInstance();\n\n// Update and persist objects with a thread-safe transaction\nrealm.Write(() =>\n{\n    realm.Add(new Dog { Name = "Rex", Age = 1 });\n});',
            position: "1",
          },
        ],
      },
    ],
  },
  {
    content: (
      <>
        <h3>Build full mobile apps with ease</h3>
        <p>
          Realm Sync handles the most complex part of connecting your client to
          the backend with ease – guaranteeing an app that works as expected and
          syncs data reliably, every time.
        </p>
      </>
    ),
    codeLanguages: [
      {
        name: "Kotlin",
        snippets: [
          {
            code: `import io.realm.kotlin.where\n\nopen class Dog(_name: String = "Dog") : RealmObject() {\n  @PrimaryKey var _id: ObjectId = ObjectId()\n  var name = _name\n  var age = 0\n}\nfun example() {\n  val app = App(\n  AppConfiguration.Builder("Your App Id")\n .build()\n )\n  // MongoDB Realm offers built-in Auth to secure Syncing data\n  app.loginAsync(Credentials.anonymous()) {\n\n    // Data can be Synced with a simple configuration\n    val config = SyncConfiguration.Builder(app.currentUser(),\n      "myPartition").build()\n\n      val realm = Realm.getInstance(config)\n  }\n}`,
            position: "1",
          },
        ],
      },
      {
        name: "Swift",
        snippets: [
          {
            code: `class Dog: Object {\n  @Persisted(primaryKey: true) var _id = 0\n  @Persisted var name = ""\n  @Persisted var age = 0\n}\n\nfunc example() async {\n let app = App(id: "Your App ID")\n\n  // MongoDB Realm offers built-in Auth to securely Sync data\n let user = try! await app.login(credentials: Credentials.anonymous)\n\n // Data can be Synced with a simple configuration\n let realm = try! await Realm(\n\n   configuration: user.configuration(partitionValue: "myPartition")\n\n  // Realm writes are transactional and Sync automatically\n  try! realm.write {\n    let dog = Dog()\n   dog.name = "Princess Gracie"\n   dog.age = 6\n    realm.add(dog)\n  }\n // Data Synced onto a device can be queried locally\n let olderDogs = realm.objects(Dog.self)\n   .filter("age > 5")\n}`,
            position: "1",
          },
        ],
      },
      {
        name: "JavaScript",
        snippets: [
          {
            code: `const DogSchema = {\n  name: "Dog",\n  properties: {\n   _id: "objectId",\n    name: "string",\n   age: "int",\n   breed: "string?"\n  },\n  primaryKey: '_id',\n};\n\nasync function example() {\n  const app = new Realm.App({ id: "<Your App ID>" });\n\n // MongoDB Realm offers built-in Auth to secure Syncing data\n  const credentials = Realm.Credentials.anonymous();\n  await app.logIn(credentials);\n\n // Data can be Synced with a simple configuration\n const config = {\n  schema: [DogSchema],\n    sync: {\n     user: app.currentUser,\n    partitionValue: "MyPartitionValue"\n    },\n  };\n\n  const realm = await Realm.open(config);\n\n // Realm Writes are transactional and Sync automatically\n  realm.write(() => {\n   realm.create("Dog", {\n     _id: new ObjectId(),\n      name: "Princess Gracie",\n      age: 6,\n   });\n });\n // Data Synced onto a device can be queried locally\n const allDogs = realm.objects("Dog");\n const olderDogs = alLDogs.filtered("age > 5");\n}`,
            position: "1",
          },
        ],
      },
      {
        name: "C#",
        snippets: [
          {
            code: `public class Dog : RealmObject\n {\n   [PrimaryKey]\n    [MapTo("_id")]\n    public ObjectId Id { get; set; } = ObjectId.GenerateNewId();\n    public string Name { get; set; }\n    public int Age { get; set; }\n  }\n\n public async void SyncedDogs()\n  {\n    var app = App.Create("<Your App ID>");\n\n    // MongoDB Realm offers built-in Auth to secure Syncing data\n    var user = await app.LogInAsync(Credentials.Anonymous());\n\n   // Data can be Synced with a simple configuration\n    var config = new SyncConfiguration("myPartition", user);\n    var realm = await Realm.GetInstanceAsync(config);\n\n   // Realm Writes are transactional and Sync automatically\n    realm.Write(() =>\n   {\n     realm.Add(new Dog()\n     {\n       Age = 6,\n        Name = "Princess Gracie"\n      });\n   });\n\n   // Data Synced onto a device can be queried locally\n   var olderDogs = realm.All<Dog>()\n      .Where(d => d.Age > 5\n     .ToList();\n    }\n }`,
            position: "1",
          },
        ],
      },
    ],
  },
]

const header = {
  title: "Build better apps, faster.",
  content: (
    <>
      <p>
        Realm is a fast, scalable alternative to SQLite with mobile to cloud data sync that makes building real-time, reactive mobile apps easy.
      </p>
    </>
  ),
  links: [
    {
      text: "Start building locally",
      href: "https://docs.mongodb.com/realm/sdk/",
    },
    {
      text: "Deploy a mobile backend in the cloud",
      href: "https://www.mongodb.com/realm/register",
    },
  ],
  image:
    "https://webimages.mongodb.com/_com_assets/cms/l1ch10z1aeh731mro-home_background-new-25.svg?auto=format%252Ccompress",
  customBleed: 105
}

const IndexPage = () => {
  const data = useStaticQuery(graphql`
    query {
      allEventCalendar {
        edges {
          node {
            topic_list {
              topics {
                id
                title
                event {
                  start
                  timezone
                }
              }
            }
          }
        }
      }
      allDevhubContent {
        edges {
          node {
            version
            title
            items {
              content_text
              id
              url
              title
            }
          }
        }
      }
    }
  `)

  return (
    <Layout header={header} endCap={{ ...defaultEndCap, backgroundColor: "#F9F9FF" }}>
      <SEO title="Realm Home" description="Build better apps, faster"></SEO>
      <ContentBlock>
        <h2>Why Use Realm?</h2>
        <CardGrid cards={topCards} cardWidth="360" checked />
      </ContentBlock>
      <ContentBlock backgroundColor="rgba(241, 240, 252, 0.35)">
        {slaloms.map((slalom, i) => {
          return (
            <Slalom
              content={slalom.content}
              codeLanguages={slalom.codeLanguages}
              key={`slalom-${i}`}
            />
          )
        })}
      </ContentBlock>

      <ContentBlock>
        <SocialProof
          brands="all"
          header="Apps built with Realm"
          github
          content={
            <p>
              Thousands of apps are built on Realm — from hyper-growth startups
              to Fortune 500 companies.
            </p>
          }
        />
      </ContentBlock>

      <ContentBlock backgroundColor="rgba(241, 240, 252, 0.35)">
        <EventCalendar
          content={
            <>
              <h2>Upcoming events & meetups</h2>
              <p>
                Join us for regular meetups and find out where our Engineers and
                Advocates are speaking at key events.
              </p>
              <p>
                Missed a Realm event or meetup? Head over to our Youtube channel
                to catch up on the meetups and livestreams with our Realm team
                and community members.
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                }}
              >
                <CustomLink
                  href="https://www.mongodb.com/community/forums/c/realm/9/l/calendar"
                  text="View full calendar"
                />
                <CustomLink
                  href="https://www.youtube.com/playlist?list=PLZWjyme3DCQEmazrRG9qrlgdsNlAJDYKn"
                  text="Watch videos"
                />
              </div>
            </>
          }
          data={data.allEventCalendar.edges[0].node.topic_list.topics}
          numberOfEvents={3}
        />
      </ContentBlock>

      <ContentBlock>
        <h2>Getting Started</h2>

        <CardGrid
          cards={middleCards}
          cardWidth="560"
          border
          background="rgba(241, 240, 252, 0.35)"
        />
      </ContentBlock>
      <ContentBlock>
        <h2>Recent articles</h2>

        <DeveloperContent
          data={data.allDevhubContent.edges[0].node.items}
          limit={3}
        />
      </ContentBlock>

      <ContentBlock backgroundColor="rgba(241, 240, 252, 0.35)">
        <h2>Frequently asked questions</h2>

        <CardGrid cards={bottomCards} cardWidth="500" />
      </ContentBlock>
    </Layout>
  )
}

export default IndexPage
